import React from 'react';
import { X, BarChart3, Info, Truck, Package, DollarSign, Calendar } from 'lucide-react';

// Decode HTML entities in URLs (legacy data may have &#x2F; instead of /)
const decodeUrl = (url) => {
  if (!url) return url;
  const txt = document.createElement('textarea');
  txt.innerHTML = url;
  return txt.value;
};

const PartViewModal = ({ part, onClose }) => {
  if (!part) return null;

  const getStockStatus = (quantity, minStock) => {
    if (quantity === 0) return { status: 'Out of Stock', color: 'bg-red-100 text-red-800' };
    if (quantity <= minStock) return { status: 'Low Stock', color: 'bg-yellow-100 text-yellow-800' };
    return { status: 'In Stock', color: 'bg-green-100 text-green-800' };
  };

  const stockStatus = getStockStatus(part.quantity, part.minStock);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center space-x-3">
            <Package className="w-6 h-6 text-blue-600" />
            <div>
              <h3 className="text-xl font-bold text-gray-900">{part.name}</h3>
              <p className="text-sm text-gray-600">{part.sku && `SKU: ${part.sku}`}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Image and Basic Info */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-1">
              {part.imageUrl ? (
                <img
                  src={decodeUrl(part.imageUrl)}
                  alt={part.name}
                  className="w-full h-64 object-contain rounded-lg border shadow-md bg-white"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className="w-full h-64 bg-gray-100 rounded-lg border flex items-center justify-center" style={{display: part.imageUrl ? 'none' : 'flex'}}>
                <Package className="w-16 h-16 text-gray-400" />
              </div>
            </div>
            <div className="lg:col-span-2 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <BarChart3 className="w-5 h-5 text-blue-600" />
                    <h4 className="font-semibold text-blue-900">Stock Status</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Current Stock:</span>
                      <span className="font-medium">{part.quantity} {part.unit}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Status:</span>
                      <span className={`px-2 py-1 text-xs rounded-full ${stockStatus.color}`}>
                        {stockStatus.status}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Min Stock:</span>
                      <span className="font-medium">{part.minStock}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Max Stock:</span>
                      <span className="font-medium">{part.maxStock || 'N/A'}</span>
                    </div>
                  </div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <DollarSign className="w-5 h-5 text-green-600" />
                    <h4 className="font-semibold text-green-900">Pricing</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Cost Price:</span>
                      <span className="font-medium">${part.costPrice || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Selling Price:</span>
                      <span className="font-medium text-green-700">${part.price || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">B2B Margin:</span>
                      <span className="font-medium">{part.b2bMargin || 0}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">B2C Margin:</span>
                      <span className="font-medium">{part.b2cMargin || 0}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Detailed Information Sections */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Product Information */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-center space-x-2 mb-4">
                <Info className="w-5 h-5 text-gray-600" />
                <h4 className="font-semibold text-gray-900">Product Information</h4>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Brand:</span>
                  <span className="font-medium">{part.brand || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Model:</span>
                  <span className="font-medium">{part.model || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Category:</span>
                  <span className="font-medium">{part.category || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Sub Category:</span>
                  <span className="font-medium">{part.subCategory || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Barcode:</span>
                  <span className="font-medium">{part.barcode || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Unit:</span>
                  <span className="font-medium">{part.unit}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Weight:</span>
                  <span className="font-medium">{part.weight ? `${part.weight} kg` : 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Dimensions:</span>
                  <span className="font-medium">{part.dimensions || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Vendor & Location */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-center space-x-2 mb-4">
                <Truck className="w-5 h-5 text-gray-600" />
                <h4 className="font-semibold text-gray-900">Vendor & Location</h4>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Vendor:</span>
                  <span className="font-medium">{part.vendor?.name || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Location:</span>
                  <span className="font-medium">{part.location || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Status:</span>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    part.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 
                    part.status === 'INACTIVE' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {part.status}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Trackable:</span>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    part.isTrackable ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {part.isTrackable ? 'Yes' : 'No'}
                  </span>
                </div>
                {part.expiryDate && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Expiry Date:</span>
                    <span className="font-medium">{new Date(part.expiryDate).toLocaleDateString()}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Tax Rate:</span>
                  <span className="font-medium">{part.taxRate || 0}%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Description & Notes */}
          {(part.description || part.notes) && (
            <div className="mt-6 space-y-4">
              {part.description && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-blue-900 mb-2">Description</h4>
                  <p className="text-gray-700">{part.description}</p>
                </div>
              )}
              {part.notes && (
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-yellow-900 mb-2">Notes</h4>
                  <p className="text-gray-700">{part.notes}</p>
                </div>
              )}
            </div>
          )}

          {/* Timestamps */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="flex items-center space-x-2 mb-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Timeline</span>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
              <div>
                <span className="font-medium">Created:</span> {new Date(part.createdAt).toLocaleString()}
              </div>
              <div>
                <span className="font-medium">Updated:</span> {new Date(part.updatedAt).toLocaleString()}
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PartViewModal;